import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { resetState as resetActionableNormal } from "../features/actionable/actionableNormalSlice";
import { resetState as resetActionableOverdue } from "../features/actionable/actionableOverdueSlice";
import { signOut } from "../features/authentication/authenticationSlice";
import { resetState as resetStateDueDate } from "../features/billingAndCollection/dueDateExtensionNormalSlice";
import { resetState as resetStateInstallmentsNormal } from "../features/billingAndCollection/installmentsNormalSlice";
import { resetState as resetStateInstallmentsOverdue } from "../features/billingAndCollection/installmentsOverdueSlice";
import { resetOverallPerformanceState } from "../features/dashboard/overallPerformance";
import { resetState as resetStateColNormal } from "../features/generalServices/colNormalSlice";
import { resetState as resetStateColOverdue } from "../features/generalServices/colOverdueSlice";
import { resetState as resetStateConNormal } from "../features/generalServices/coaNormalSlice";
import { resetState as resetStateConOverdue } from "../features/generalServices/coaOverdueSlice";
import { resetState as resetStateCotNormal } from "../features/generalServices/cotNormalSlice";
import { resetState as resetStateCotOverdue } from "../features/generalServices/cotOverdueSlice";
import { resetState as resetStateNcNormal } from "../features/generalServices/ncNormalSlice";
import { resetState as resetStateNcOverdue } from "../features/generalServices/ncOverdueSlice";
import { resetFeederStatusState } from "../features/geoJson/feedersStatus";
import { resetGeoJsonState } from "../features/geoJson/geoJson";
import { removeStats } from "../features/stats/statsSlice";
import { cfpBaseUrl } from "../network/urls";
import { resetState as resetStateAllComplaints } from "../features/powerComplaints/getComplaintSlice";

const ACTIVITY_THROTTLE_MS = 60 * 1000;

const useInactivityLogout = (timeout = 30 * 60 * 1000) => {
  const dispatch = useDispatch();
  const timerRef = useRef(null);
  const lastActivityRef = useRef(0);
  const tokenRef = useRef("");
  const { token } = useSelector((state) => state.auth);

  tokenRef.current = token;

  useEffect(() => {
    if (!token) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return undefined;
    }

    const callLogoutAPI = async () => {
      try {
        await fetch(`${cfpBaseUrl}customerFaciliationPortal/logOut`, {
          method: "PUT",
          headers: { Authorization: tokenRef.current },
          keepalive: true,
        });
      } catch {
        // ignore
      }
    };

    const logout = async () => {
      try {
        await callLogoutAPI();

        dispatch(resetStateNcNormal());
        dispatch(resetStateNcOverdue());
        dispatch(resetStateConNormal());
        dispatch(resetStateConOverdue());
        dispatch(resetStateColNormal());
        dispatch(resetStateColOverdue());
        dispatch(resetStateCotNormal());
        dispatch(resetStateCotOverdue());
        dispatch(resetStateDueDate());
        dispatch(resetStateInstallmentsNormal());
        dispatch(resetStateInstallmentsOverdue());
        dispatch(removeStats());
        dispatch(resetOverallPerformanceState());
        dispatch(resetFeederStatusState());
        dispatch(resetGeoJsonState());
        dispatch(resetActionableNormal());
        dispatch(resetActionableOverdue());
        dispatch(resetStateAllComplaints());
        dispatch(signOut());

        localStorage.clear();
        sessionStorage.clear();
        window.location.href = "/";
      } catch {
        // ignore
      }
    };

    const scheduleLogout = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(logout, timeout);
    };

    const markActivity = (force = false) => {
      const now = Date.now();
      if (!force && now - lastActivityRef.current < ACTIVITY_THROTTLE_MS) {
        return;
      }
      lastActivityRef.current = now;
      scheduleLogout();
    };

    const onPassiveActivity = () => markActivity(false);
    const onActiveInput = () => markActivity(true);

    markActivity(true);

    const passiveOptions = { passive: true };
    window.addEventListener("mousemove", onPassiveActivity, passiveOptions);
    window.addEventListener("scroll", onPassiveActivity, passiveOptions);
    window.addEventListener("touchstart", onPassiveActivity, passiveOptions);
    window.addEventListener("mousedown", onActiveInput);
    window.addEventListener("keydown", onActiveInput);

    return () => {
      window.removeEventListener("mousemove", onPassiveActivity);
      window.removeEventListener("scroll", onPassiveActivity);
      window.removeEventListener("touchstart", onPassiveActivity);
      window.removeEventListener("mousedown", onActiveInput);
      window.removeEventListener("keydown", onActiveInput);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [dispatch, timeout, token]);
};

export default useInactivityLogout;
